// src/components/AudioPlayer.tsx

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { AudioWithUrl } from '../types/audio.types';

interface AudioPlayerProps {
  audio: AudioWithUrl | null;
  onClose?: () => void;
}

type PlayerState = 'idle' | 'loading' | 'buffering' | 'ready' | 'playing' | 'paused' | 'error';

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audio, onClose }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLInputElement>(null);
  
  const [playerState, setPlayerState] = useState<PlayerState>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [showAllMetadata, setShowAllMetadata] = useState(false);

  // Reset states quando trocar de áudio
  useEffect(() => {
    setPlayerState('loading');
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setIsSeeking(false);
  }, [audio?.audioId]);

  // Volume sempre no máximo
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 1.0;
    }
  }, [audio]);

  const togglePlayPause = useCallback(async () => {
    if (!audioRef.current || playerState === 'error' || playerState === 'loading') return;

    try {
      if (playerState === 'playing') {
        audioRef.current.pause();
      } else {
        await audioRef.current.play();
      }
    } catch (err) {
      console.error('Erro ao reproduzir áudio:', err);
      setError('Erro ao reproduzir o áudio. Tente novamente.');
    }
  }, [playerState]);

  const seekRelative = useCallback((seconds: number) => {
    if (!audioRef.current) return;
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration, currentTime]);

  // Event listeners do áudio
  useEffect(() => {
    if (!audio || !audioRef.current) return;

    const audioElement = audioRef.current;

    const handleLoadStart = () => {
      setPlayerState('loading');
      setError(null);
    };

    const handleCanPlay = () => {
      if (playerState === 'loading') {
        setPlayerState('ready');
      }
    };

    const handleCanPlayThrough = () => {
      setPlayerState('ready');
    };

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audioElement.currentTime);
      }
    };

    const handleLoadedMetadata = () => {
      setDuration(audioElement.duration);
      setError(null);
    };

    const handlePlay = () => {
      setPlayerState('playing');
    };

    const handlePause = () => {
      setPlayerState('paused');
    };

    const handleEnded = () => {
      setPlayerState('paused');
      setCurrentTime(0);
      audioElement.currentTime = 0;
    };

    const handleWaiting = () => {
      setPlayerState('buffering');
    };

    const handlePlaying = () => {
      setPlayerState('playing');
    };

    const handleError = (e: ErrorEvent) => {
      const mediaError = audioElement.error;
      let errorMessage = 'Erro ao carregar o áudio.';
      
      if (mediaError) {
        switch (mediaError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMessage = 'Carregamento do áudio foi interrompido.';
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMessage = 'Erro de rede ao carregar o áudio. Verifique sua conexão.';
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMessage = 'Erro ao decodificar o áudio. O arquivo pode estar corrompido.';
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Formato de áudio não suportado ou URL inválida.';
            break;
          default:
            errorMessage = `Erro desconhecido (código: ${mediaError.code})`;
        }
        
        if (mediaError.code === MediaError.MEDIA_ERR_NETWORK || 
            mediaError.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          errorMessage += ' A URL pode ter expirado - tente recarregar a página.';
        }
      }
      
      setError(errorMessage);
      setPlayerState('error');
    };

    audioElement.addEventListener('loadstart', handleLoadStart);
    audioElement.addEventListener('canplay', handleCanPlay);
    audioElement.addEventListener('canplaythrough', handleCanPlayThrough);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('waiting', handleWaiting);
    audioElement.addEventListener('playing', handlePlaying);
    audioElement.addEventListener('error', handleError as any);

    return () => {
      audioElement.removeEventListener('loadstart', handleLoadStart);
      audioElement.removeEventListener('canplay', handleCanPlay);
      audioElement.removeEventListener('canplaythrough', handleCanPlayThrough);
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('waiting', handleWaiting);
      audioElement.removeEventListener('playing', handlePlaying);
      audioElement.removeEventListener('error', handleError as any);
    };
  }, [audio, playerState, isSeeking]);

  // Suporte a teclado (removido controle de volume)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!audio || !audioRef.current) return;
      
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          seekRelative(-5);
          break;
        case 'ArrowRight':
          e.preventDefault();
          seekRelative(5);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [audio, togglePlayPause, seekRelative]);

  const handleSeekStart = () => {
    setIsSeeking(true);
  };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
  };

  const handleSeekMouseUp = () => {
    if (progressBarRef.current && audioRef.current) {
      const newTime = parseFloat(progressBarRef.current.value);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
    setIsSeeking(false);
  };

  const handleSeekTouchEnd = () => {
    if (progressBarRef.current && audioRef.current) {
      const newTime = parseFloat(progressBarRef.current.value);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
    setIsSeeking(false);
  };

  const formatTime = (seconds: number): string => {
    if (!isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStateIndicator = () => {
    switch (playerState) {
      case 'loading':
        return (
          <div className="flex items-center justify-center gap-2 text-indigo-600 text-sm">
            <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
            <span>Carregando áudio...</span>
          </div>
        );
      case 'buffering':
        return (
          <div className="flex items-center justify-center gap-2 text-amber-600 text-sm">
            <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-amber-600 border-t-transparent"></div>
            <span>Bufferizando...</span>
          </div>
        );
      case 'error':
        return null;
      default:
        return null;
    }
  };

  if (!audio) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-center text-gray-500">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
            />
          </svg>
          <p className="mt-2 text-sm">Selecione um áudio para reproduzir</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Player de Áudio</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded"
            aria-label="Fechar player"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Metadados Principais */}
      <div className="mb-4 p-4 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-600">ID Paciente:</span>
            <span className="ml-2 font-medium text-gray-900">{audio.subject_id}</span>
          </div>
          <div>
            <span className="text-gray-600">Foco:</span>
            <span className="ml-2 font-medium text-gray-900">{audio.focus}</span>
          </div>
          <div>
            <span className="text-gray-600">Faixa Etária:</span>
            <span className="ml-2 font-medium text-gray-900">{audio.age || audio.age_group}</span>
          </div>
          <div>
            <span className="text-gray-600">Sexo:</span>
            <span className="ml-2 font-medium text-gray-900">{audio.biological_sex}</span>
          </div>
          {audio.height_cm && (
            <div>
              <span className="text-gray-600">Altura:</span>
              <span className="ml-2 font-medium text-gray-900">{audio.height_cm} cm</span>
            </div>
          )}
          {audio.weight_kg && (
            <div>
              <span className="text-gray-600">Peso:</span>
              <span className="ml-2 font-medium text-gray-900">{audio.weight_kg} kg</span>
            </div>
          )}
          {audio.pregnancy_status !== null && (
            <div>
              <span className="text-gray-600">Gravidez:</span>
              <span className="ml-2 font-medium text-gray-900">
                {audio.pregnancy_status ? 'Sim' : 'Não'}
              </span>
            </div>
          )}
          <div>
            <span className="text-gray-600">Outcome:</span>
            <span className={`ml-2 font-medium ${
              audio.outcome === 'Normal' ? 'text-green-600' : 
              audio.outcome === 'Abnormal' ? 'text-red-600' : 
              'text-gray-900'
            }`}>
              {audio.outcome}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Campanha:</span>
            <span className="ml-2 font-medium text-gray-900">{audio.campaign}</span>
          </div>
        </div>

        {/* Toggle para mostrar todos os metadados */}
        <button
          onClick={() => setShowAllMetadata(!showAllMetadata)}
          className="mt-3 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
        >
          {showAllMetadata ? (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              Ocultar metadados completos do CSV
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Mostrar TODOS os 23 campos do CSV CirCor
            </>
          )}
        </button>

        {/* Metadados Completos */}
        {showAllMetadata && (
          <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
            {/* SEÇÃO 1: Dados Demográficos Completos */}
            <div className="bg-white p-3 rounded border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                Dados Demográficos Completos (CSV)
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="col-span-2">
                  <span className="text-gray-600">Patient ID:</span>
                  <span className="ml-2 font-medium text-gray-900">{audio.subject_id}</span>
                </div>
                {audio.additional_id && (
                  <div className="col-span-2">
                    <span className="text-gray-600">Additional ID:</span>
                    <span className="ml-2 font-medium text-gray-900">{audio.additional_id}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-600">Age (CSV):</span>
                  <span className="ml-2 font-medium text-gray-900">{audio.age || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Sex:</span>
                  <span className="ml-2 font-medium text-gray-900">{audio.biological_sex}</span>
                </div>
                <div>
                  <span className="text-gray-600">Height:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {audio.height_cm ? `${audio.height_cm} cm` : 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Weight:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {audio.weight_kg ? `${audio.weight_kg} kg` : 'N/A'}
                  </span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Pregnancy status:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {audio.pregnancy_status === null ? 'N/A' : audio.pregnancy_status ? 'True' : 'False'}
                  </span>
                </div>
              </div>
            </div>

            {/* SEÇÃO 2: Localização e Gravação */}
            <div className="bg-white p-3 rounded border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Localização e Foco Cardíaco (CSV)
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="col-span-2">
                  <span className="text-gray-600">Recording locations:</span>
                  <span className="ml-2 font-medium text-gray-900">{audio.recording_locations}</span>
                </div>
                <div>
                  <span className="text-gray-600">Focus (atual):</span>
                  <span className="ml-2 font-medium text-indigo-600">{audio.focus}</span>
                </div>
                <div>
                  <span className="text-gray-600">Audio ID:</span>
                  <span className="ml-2 font-mono text-xs text-gray-900">{audio.audioId}</span>
                </div>
              </div>
            </div>

            {/* SEÇÃO 3: Murmúrio Geral (do CSV) */}
            <div className="bg-white p-3 rounded border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <svg className="w-4 h-4 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Murmúrio Cardíaco - Geral (CSV)
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Murmur:</span>
                  <span className={`ml-2 font-medium ${
                    audio.murmur === 'Present' ? 'text-orange-600' :
                    audio.murmur === 'Absent' ? 'text-green-600' :
                    'text-gray-600'
                  }`}>{audio.murmur || 'Unknown'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Most audible location:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {audio.most_audible_location || 'N/A'}
                  </span>
                </div>
                {audio.murmur_locations && (
                  <div className="col-span-2">
                    <span className="text-gray-600">Murmur locations:</span>
                    <span className="ml-2 font-medium text-gray-900">{audio.murmur_locations}</span>
                  </div>
                )}
              </div>
            </div>

            {/* SEÇÃO 4: Murmúrio Sistólico - TODOS OS CAMPOS CSV */}
            {(audio.systolic_murmur_timing || audio.systolic_murmur_shape || 
              audio.systolic_murmur_grading || audio.systolic_murmur_pitch || 
              audio.systolic_murmur_quality) && (
              <div className="bg-white p-3 rounded border border-orange-200">
                <h4 className="text-xs font-semibold text-orange-700 mb-2 flex items-center gap-1">
                  <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Murmúrio Sistólico - Detalhes Completos (CSV)
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Timing:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.systolic_murmur_timing || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Shape:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.systolic_murmur_shape || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Grading:</span>
                    <span className="ml-2 font-medium text-orange-600">
                      {audio.systolic_murmur_grading || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Pitch:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.systolic_murmur_pitch || 'N/A'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Quality:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.systolic_murmur_quality || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* SEÇÃO 5: Murmúrio Diastólico - TODOS OS CAMPOS CSV */}
            {(audio.diastolic_murmur_timing || audio.diastolic_murmur_shape || 
              audio.diastolic_murmur_grading || audio.diastolic_murmur_pitch || 
              audio.diastolic_murmur_quality) && (
              <div className="bg-white p-3 rounded border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Murmúrio Diastólico - Detalhes Completos (CSV)
                </h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-600">Timing:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.diastolic_murmur_timing || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Shape:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.diastolic_murmur_shape || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Grading:</span>
                    <span className="ml-2 font-medium text-blue-600">
                      {audio.diastolic_murmur_grading || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Pitch:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.diastolic_murmur_pitch || 'N/A'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">Quality:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {audio.diastolic_murmur_quality || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* SEÇÃO 6: Resultado Clínico e Campanha */}
            <div className="bg-white p-3 rounded border border-green-200">
              <h4 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Resultado Clínico (CSV)
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Outcome:</span>
                  <span className={`ml-2 font-semibold ${
                    audio.outcome === 'Normal' ? 'text-green-600' : 
                    audio.outcome === 'Abnormal' ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {audio.outcome || 'Unknown'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Campaign:</span>
                  <span className="ml-2 font-medium text-gray-900">{audio.campaign || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* SEÇÃO 7: Dados Técnicos */}
            <div className="bg-white p-3 rounded border border-gray-200">
              <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
                </svg>
                Dados Técnicos do Áudio
              </h4>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-600">Sample Rate:</span>
                  <span className="ml-2 font-mono text-gray-900">{audio.sample_rate_hz} Hz</span>
                </div>
                <div>
                  <span className="text-gray-600">Bit Depth:</span>
                  <span className="ml-2 font-mono text-gray-900">{audio.bit_depth || 16} bit</span>
                </div>
                <div>
                  <span className="text-gray-600">Canais:</span>
                  <span className="ml-2 font-mono text-gray-900">{audio.channels || 1}</span>
                </div>
                <div>
                  <span className="text-gray-600">Dataset:</span>
                  <span className="ml-2 font-mono text-xs text-gray-900">{audio.dataset_origin}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">GCS Path:</span>
                  <span className="ml-2 font-mono text-xs text-gray-700 break-all">{audio.gcs_path}</span>
                </div>
                {audio.tsv_path && (
                  <div className="col-span-2">
                    <span className="text-gray-600">TSV Path:</span>
                    <span className="ml-2 font-mono text-xs text-gray-700 break-all">{audio.tsv_path}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Indicadores de Estado */}
      {getStateIndicator()}

      {/* Mensagem de Erro */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" role="alert">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Áudio HTML5 */}
      {audio.signedUrl && (
        <audio 
          ref={audioRef} 
          src={audio.signedUrl} 
          preload="metadata"
          aria-label={`Áudio do paciente ${audio.subject_id}, foco ${audio.focus}`}
        />
      )}

      {/* Controles do Player */}
      <div className="space-y-4">
        {/* Barra de Progresso */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-600 w-12 text-right" aria-label="Tempo atual">
            {formatTime(currentTime)}
          </span>
          <input
            ref={progressBarRef}
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeekChange}
            onMouseDown={handleSeekStart}
            onMouseUp={handleSeekMouseUp}
            onTouchStart={handleSeekStart}
            onTouchEnd={handleSeekTouchEnd}
            disabled={playerState === 'loading' || playerState === 'error'}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Barra de progresso do áudio"
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-valuenow={currentTime}
            aria-valuetext={`${formatTime(currentTime)} de ${formatTime(duration)}`}
          />
          <span className="text-xs text-gray-600 w-12" aria-label="Duração total">
            {formatTime(duration)}
          </span>
        </div>

        {/* Botões de Controle - CORRIGIDO */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={togglePlayPause}
            disabled={!audio.signedUrl || playerState === 'loading' || playerState === 'error'}
            className="flex items-center justify-center w-16 h-16 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed shadow-lg focus:outline-none focus:ring-4 focus:ring-indigo-300"
            aria-label={playerState === 'playing' ? 'Pausar áudio' : 'Reproduzir áudio'}
            title={playerState === 'playing' ? 'Pausar (Espaço)' : 'Reproduzir (Espaço)'}
          >
            {playerState === 'playing' ? (
              // Ícone de Pause - Simples e claro
              <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              // Ícone de Play - Simples e claro
              <svg className="w-8 h-8 ml-1" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Atalhos de Teclado */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <details className="text-xs text-gray-600">
          <summary className="cursor-pointer hover:text-gray-900 font-medium">
            Atalhos de teclado
          </summary>
          <ul className="mt-2 space-y-1 ml-4 list-disc">
            <li><kbd className="px-1 py-0.5 bg-gray-100 rounded">Espaço</kbd> - Play/Pause</li>
            <li><kbd className="px-1 py-0.5 bg-gray-100 rounded">←</kbd> - Retroceder 5s</li>
            <li><kbd className="px-1 py-0.5 bg-gray-100 rounded">→</kbd> - Avançar 5s</li>
          </ul>
        </details>
      </div>
    </div>
  );
};

export default AudioPlayer;
